<script setup>
import { ref, computed, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['close']);

const { SELECTABLE_AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentAvailability = ref(
  props.availability === 'busy' ? 'offline' : props.availability
);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentCredentials = ref({ email: props.email });

// State for teams and inboxes
const selectedTeams = ref([]);
const selectedInboxes = ref([]);
const isLoadingTeams = ref(false);
const isLoadingInboxes = ref(false);

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');
const allTeams = useMapGetter('teams/getTeams');
const allInboxes = useMapGetter('inboxes/getInboxes');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: SELECTABLE_AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === SELECTABLE_AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    // Update agent basic info
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);

    // Update team assignments
    await updateTeamAssignments();

    // Update inbox assignments
    await updateInboxAssignments();

    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};

// Load teams and inboxes on mount
onMounted(async () => {
  await Promise.all([
    store.dispatch('teams/get'),
    store.dispatch('inboxes/get'),
  ]);
  await loadAgentTeamsAndInboxes();
});

// Load agent's current teams and inboxes
const loadAgentTeamsAndInboxes = async () => {
  // Load teams
  isLoadingTeams.value = true;
  try {
    const teamsWithAgent = [];
    for (const team of allTeams.value) {
      try {
        await store.dispatch('teamMembers/get', {
          teamId: team.id,
        });
        // Get the team members from the store
        const teamMembers = store.getters['teamMembers/getTeamMembers'](
          team.id
        );
        const isMember = teamMembers.some(member => member.id === props.id);
        if (isMember) {
          teamsWithAgent.push(team.id);
        }
      } catch (error) {
        // Continue with next team
      }
    }
    selectedTeams.value = teamsWithAgent;
    initialTeams.value = [...teamsWithAgent]; // Store initial state
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.ERROR_LOADING_TEAMS'));
  } finally {
    isLoadingTeams.value = false;
  }

  // Load inboxes
  isLoadingInboxes.value = true;
  try {
    const inboxesWithAgent = [];
    for (const inbox of allInboxes.value) {
      try {
        const response = await store.dispatch('inboxMembers/get', {
          inboxId: inbox.id,
        });
        const {
          data: { payload: inboxMembers },
        } = response;
        const isMember = inboxMembers.some(member => member.id === props.id);
        if (isMember) {
          inboxesWithAgent.push(inbox.id);
        }
      } catch (error) {
        // Continue with next inbox
      }
    }
    selectedInboxes.value = inboxesWithAgent;
    initialInboxes.value = [...inboxesWithAgent]; // Store initial state
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.ERROR_LOADING_INBOXES'));
  } finally {
    isLoadingInboxes.value = false;
  }
};

// Store initial teams and inboxes to compare changes
const initialTeams = ref([]);
const initialInboxes = ref([]);

// Update team assignments when form is submitted
const updateTeamAssignments = async () => {
  // Find teams that were added or removed
  const teamsToAdd = selectedTeams.value.filter(
    teamId => !initialTeams.value.includes(teamId)
  );
  const teamsToRemove = initialTeams.value.filter(
    teamId => !selectedTeams.value.includes(teamId)
  );

  // Update each team
  for (const teamId of [...teamsToAdd, ...teamsToRemove]) {
    try {
      await store.dispatch('teamMembers/get', { teamId });
      const currentMembers =
        store.getters['teamMembers/getTeamMembers'](teamId);

      const shouldIncludeAgent = selectedTeams.value.includes(teamId);
      const agentIds = currentMembers.map(member => member.id);

      let updatedMembers;
      if (shouldIncludeAgent && !agentIds.includes(props.id)) {
        // Add agent
        updatedMembers = [...agentIds, props.id];
      } else if (!shouldIncludeAgent && agentIds.includes(props.id)) {
        // Remove agent
        updatedMembers = agentIds.filter(id => id !== props.id);
      } else {
        // No change needed
        continue;
      }

      await store.dispatch('teamMembers/update', {
        teamId,
        agentsList: updatedMembers,
      });
    } catch (error) {
      // Continue with other teams even if one fails
      console.error(`Error updating team ${teamId}:`, error);
    }
  }
};

// Update inbox assignments when form is submitted
const updateInboxAssignments = async () => {
  // Find inboxes that were added or removed
  const inboxesToAdd = selectedInboxes.value.filter(
    inboxId => !initialInboxes.value.includes(inboxId)
  );
  const inboxesToRemove = initialInboxes.value.filter(
    inboxId => !selectedInboxes.value.includes(inboxId)
  );

  // Update each inbox
  for (const inboxId of [...inboxesToAdd, ...inboxesToRemove]) {
    try {
      const response = await store.dispatch('inboxMembers/get', { inboxId });
      const {
        data: { payload: currentMembers },
      } = response;

      const shouldIncludeAgent = selectedInboxes.value.includes(inboxId);
      const agentIds = currentMembers.map(member => member.id);

      let updatedMembers;
      if (shouldIncludeAgent && !agentIds.includes(props.id)) {
        // Add agent
        updatedMembers = [...agentIds, props.id];
      } else if (!shouldIncludeAgent && agentIds.includes(props.id)) {
        // Remove agent
        updatedMembers = agentIds.filter(id => id !== props.id);
      } else {
        // No change needed
        continue;
      }

      await store.dispatch('inboxMembers/create', {
        inboxId,
        agentList: updatedMembers,
      });
    } catch (error) {
      // Continue with other inboxes even if one fails
      console.error(`Error updating inbox ${inboxId}:`, error);
    }
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <!-- Teams Section -->
      <div class="w-full mt-4">
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ $t('AGENT_MGMT.EDIT.FORM.TEAMS.LABEL') }}
        </label>
        <p class="text-sm text-n-slate-11 mb-3">
          {{ $t('AGENT_MGMT.EDIT.FORM.TEAMS.DESCRIPTION') }}
        </p>
        <div
          v-if="isLoadingTeams"
          class="flex items-center justify-center py-4"
        >
          <span class="text-sm text-n-slate-11">{{
            $t('AGENT_MGMT.EDIT.LOADING_TEAMS')
          }}</span>
        </div>
        <div
          v-else
          class="space-y-2 max-h-48 overflow-y-auto border border-n-weak rounded-lg p-3"
        >
          <div
            v-for="team in allTeams"
            :key="team.id"
            class="flex items-center"
          >
            <input
              :id="`team-${team.id}`"
              v-model="selectedTeams"
              type="checkbox"
              :value="team.id"
              class="ltr:mr-2 rtl:ml-2 cursor-pointer"
            />
            <label :for="`team-${team.id}`" class="text-sm cursor-pointer">
              {{ team.name }}
            </label>
          </div>
          <div
            v-if="!allTeams.length"
            class="text-sm text-n-slate-11 text-center py-2"
          >
            {{ $t('AGENT_MGMT.EDIT.NO_TEAMS_AVAILABLE') }}
          </div>
        </div>
      </div>

      <!-- Inboxes Section -->
      <div class="w-full mt-4">
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ $t('AGENT_MGMT.EDIT.FORM.INBOXES.LABEL') }}
        </label>
        <p class="text-sm text-n-slate-11 mb-3">
          {{ $t('AGENT_MGMT.EDIT.FORM.INBOXES.DESCRIPTION') }}
        </p>
        <div
          v-if="isLoadingInboxes"
          class="flex items-center justify-center py-4"
        >
          <span class="text-sm text-n-slate-11">{{
            $t('AGENT_MGMT.EDIT.LOADING_INBOXES')
          }}</span>
        </div>
        <div
          v-else
          class="space-y-2 max-h-48 overflow-y-auto border border-n-weak rounded-lg p-3"
        >
          <div
            v-for="inbox in allInboxes"
            :key="inbox.id"
            class="flex items-center"
          >
            <input
              :id="`inbox-${inbox.id}`"
              v-model="selectedInboxes"
              type="checkbox"
              :value="inbox.id"
              class="ltr:mr-2 rtl:ml-2 cursor-pointer"
            />
            <label :for="`inbox-${inbox.id}`" class="text-sm cursor-pointer">
              {{ inbox.name }}
            </label>
          </div>
          <div
            v-if="!allInboxes.length"
            class="text-sm text-n-slate-11 text-center py-2"
          >
            {{ $t('AGENT_MGMT.EDIT.NO_INBOXES_AVAILABLE') }}
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2 mt-4">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
